import { useState, useRef } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Button from "Components/Common/Button/Button";
import Input from "Components/Common/Input/Input";
import Select from "Components/Common/Select/Select";
import PhoneInput from "Components/Common/PhoneInput/PhoneInput";
import CustomModal from "Components/Common/CustomModal/CustomModal";
import type { ProfileData } from "hooks/useProfile";
import { EMAIL_REGEX, CHAR_LIMIT } from "utils/Common/Constant";
import type { ProfileUpdatePayload } from "services/profileService";
import "Pages/Login/TwoFactorAuth.scss";

const OTP_LENGTH = 6;

const TIMEZONES = [
  "Eastern Time (ET)",
  "Central Time (CT)",
  "Mountain Time (MT)",
  "Pacific Time (PT)",
];

const Schema = Yup.object({
  email: Yup.string()
    .required("Email is required")
    .matches(EMAIL_REGEX, "Invalid email address")
    .max(CHAR_LIMIT.EMAIL, `Max ${CHAR_LIMIT.EMAIL} characters`),
  phone: Yup.string().max(CHAR_LIMIT.PHONE, `Max ${CHAR_LIMIT.PHONE} digits`),
  address: Yup.string().max(
    CHAR_LIMIT.ADDRESS,
    `Max ${CHAR_LIMIT.ADDRESS} characters`,
  ),
});

interface Props {
  profile: ProfileData;
  onSave: (values: ProfileUpdatePayload) => Promise<boolean>;
  saving: boolean;
  onUpdatePhone: (phone: string) => Promise<boolean>;
  onSendOTP: (phone: string) => Promise<boolean>;
  onVerifyOTP: (otp: string) => Promise<boolean>;
  onRefetchProfile: () => Promise<void>;
  sendingOTP: boolean;
  verifyingOTP: boolean;
  smsNotificationsEnabled: boolean;
  onGoToNotifications: () => void;
}

export default function ContactInfoSection({
  profile,
  onSave,
  saving,
  onUpdatePhone,
  onSendOTP,
  onVerifyOTP,
  onRefetchProfile,
  sendingOTP,
  verifyingOTP,
  smsNotificationsEnabled,
  onGoToNotifications,
}: Props) {
  const [showModal, setShowModal] = useState(false);
  const [showSmsGateModal, setShowSmsGateModal] = useState(false);
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const otp = digits.join("");
  const [otpError, setOtpError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const phoneBeingVerifiedRef = useRef<string>("");
  const [updatingPhone, setUpdatingPhone] = useState(false);

  const startCooldown = () => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((c) => {
        if (c <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const resetDigits = () => setDigits(Array(OTP_LENGTH).fill(""));

  const sendOTPAndOpenModal = async (phone: string) => {
    phoneBeingVerifiedRef.current = phone;
    const ok = await onSendOTP(phone);
    if (ok) {
      resetDigits();
      setOtpError("");
      setResendCooldown(0);
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      setShowModal(true);
      startCooldown();
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  };

  const handleVerifyClick = async (phone: string) => {
    phoneBeingVerifiedRef.current = phone;
    const stored = JSON.parse(localStorage.getItem("user") || "{}");
    const verifiedPhone = stored.phoneVerifiedAt ? stored.phone : null;
    const phoneChanged = verifiedPhone !== null && phone !== verifiedPhone;
    const gateConfirmedFor = localStorage.getItem("smsGateConfirmedFor") ?? "";
    const needsGate =
      !smsNotificationsEnabled || (phoneChanged && gateConfirmedFor !== phone);
    if (needsGate) {
      setUpdatingPhone(true);
      const ok = await onUpdatePhone(phone);
      setUpdatingPhone(false);
      if (ok) setShowSmsGateModal(true);
      return;
    }
    sendOTPAndOpenModal(phone);
  };

  const closeModal = () => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    setShowModal(false);
    resetDigits();
    setOtpError("");
  };

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    setOtpError("");
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        setDigits(next);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => {
      next[i] = ch;
    });
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleVerifyOTP = async () => {
    if (otp.length < OTP_LENGTH) {
      setOtpError("Enter all 6 digits");
      return;
    }
    const ok = await onVerifyOTP(otp);
    if (ok) closeModal();
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || sendingOTP) return;
    const ok = await onSendOTP(phoneBeingVerifiedRef.current);
    if (ok) startCooldown();
  };

  return (
    <>
      <Formik
        initialValues={{
          email: profile.email,
          phone: profile.phone,
          address: profile.address,
          timezone: profile.timezone,
        }}
        validationSchema={Schema}
        onSubmit={async (values, { resetForm }) => {
          const ok = await onSave(values);
          if (ok) resetForm({ values });
        }}
        enableReinitialize
      >
        {({ values, setFieldValue, resetForm, dirty }) => {
          const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
          const isCurrentPhoneVerified =
            !!storedUser.phoneVerifiedAt && values.phone === storedUser.phone;
          return (
            <Form>
              <div className="pf-card">
                <div className="pf-card__title">Contact Information</div>
                <Field name="email">
                  {({ field, meta }: any) => (
                    <div className="pf-field">
                      <label>
                        Email Address <span className="pf-req">*</span>
                      </label>
                      <Input
                        type="email"
                        className="pf-fi"
                        isError={meta.touched && !!meta.error}
                        {...field}
                      />
                      {meta.touched && meta.error && (
                        <p className="pf-field-error">{meta.error}</p>
                      )}
                      <div className="pf-field-help">
                        This is your login email. Changing it will require
                        re-verification.
                      </div>
                    </div>
                  )}
                </Field>
                <div className="pf-field">
                  <label>Phone Number</label>
                  <PhoneInput
                    value={values.phone}
                    onChange={(v) => setFieldValue("phone", v)}
                  />
                  <div className="pf-phone-row">
                    {isCurrentPhoneVerified ? (
                      <span className="pf-pv-badge verified">✓ Verified</span>
                    ) : (
                      <Button
                        type="button"
                        className="pf-verify-btn"
                        onClick={() => handleVerifyClick(values.phone)}
                        disabled={sendingOTP || updatingPhone}
                      >
                        {sendingOTP || updatingPhone ? (
                          <>
                            <span className="btn-spinner" /> Verifying…
                          </>
                        ) : (
                          "Verify your phone number"
                        )}
                      </Button>
                    )}
                    <div className="pf-field-help">
                      Used for SMS notifications and two-factor authentication.
                    </div>
                  </div>
                </div>
                <div className="pf-field-row pf-c2">
                  <Field name="address">
                    {({ field }: any) => (
                      <div className="pf-field">
                        <label>Office Address</label>
                        <Input className="pf-fi" type="text" {...field} />
                      </div>
                    )}
                  </Field>
                  <div className="pf-field timeZoneField">
                    <label>Time Zone</label>
                    <Select
                      className="pf-fi"
                      options={TIMEZONES.map((tz) => ({
                        value: tz,
                        label: tz,
                      }))}
                      value={values.timezone}
                      onChange={(e) =>
                        setFieldValue("timezone", e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className="pf-btn-row">
                  <Button
                    type="submit"
                    className="pf-btn-primary"
                    disabled={saving || !dirty}
                  >
                    {saving ? (
                      <>
                        <span className="btn-spinner" /> Saving…
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                  <Button
                    type="button"
                    className="pf-btn-secondary"
                    onClick={() => resetForm()}
                    disabled={saving}
                  >
                    Discard
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>

      <CustomModal
        show={showModal}
        handleClose={closeModal}
        title="Verify Phone Number"
        isFooterVisible
        footerContent={
          <div className="pf-modal-footer-row">
            <Button
              type="button"
              className="pf-resend-btn"
              onClick={handleResend}
              disabled={resendCooldown > 0 || sendingOTP}
            >
              {sendingOTP
                ? "Sending…"
                : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Resend OTP"}
            </Button>
            <Button
              className="pf-btn-primary"
              onClick={handleVerifyOTP}
              disabled={verifyingOTP}
            >
              {verifyingOTP ? (
                <>
                  <span className="btn-spinner" /> Verifying…
                </>
              ) : (
                "Verify OTP"
              )}
            </Button>
          </div>
        }
      >
        <div className="otpSec">
          <h6>Enter your OTP</h6>
          <div className="otp-boxes" onPaste={handlePaste}>
            {digits.map((digit, i) => (
              <Input
                key={i}
                ref={(el: HTMLInputElement | null) => {
                  inputRefs.current[i] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                disabled={verifyingOTP}
                autoComplete={i === 0 ? "one-time-code" : "off"}
                aria-label={`Digit ${i + 1}`}
                isError={!!otpError}
                className={`otp-box${digit ? " otp-box--filled" : ""}`}
                onChange={(e) => handleDigitChange(i, e.target.value)}
                onKeyDown={(e) => handleDigitKeyDown(i, e)}
                onFocus={(e) => e.target.select()}
              />
            ))}
          </div>
          {otpError && <p className="pf-field-error">{otpError}</p>}
        </div>
      </CustomModal>

      <CustomModal
        show={showSmsGateModal}
        handleClose={() => setShowSmsGateModal(false)}
        title="Enable SMS Notifications"
        isFooterVisible
        footerContent={
          <Button
            className="pf-btn-primary"
            onClick={async () => {
              localStorage.setItem(
                "smsGateConfirmedFor",
                phoneBeingVerifiedRef.current,
              );
              setShowSmsGateModal(false);
              await onRefetchProfile();
              onGoToNotifications();
            }}
          >
            OK
          </Button>
        }
      >
        {smsNotificationsEnabled ? (
          <>
            <p className="pf-sms-gate-primary">
              You are verifying a new phone number.
            </p>
            <p className="pf-sms-gate-sub">
              Please confirm SMS Notifications are enabled for your new number
              in the{" "}
              <strong className="pf-sms-gate-strong">
                Notification Preferences
              </strong>{" "}
              section.
            </p>
          </>
        ) : (
          <>
            <p className="pf-sms-gate-primary">
              SMS Notifications are currently disabled.
            </p>
            <p className="pf-sms-gate-sub">
              Please enable SMS Notifications from the{" "}
              <strong className="pf-sms-gate-strong">
                Notification Preferences
              </strong>{" "}
              section before verifying your phone number.
            </p>
          </>
        )}
      </CustomModal>
    </>
  );
}
